import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { 
  FileText, 
  Upload, 
  Download, 
  CheckCircle,
  AlertTriangle,
  Clock,
  Eye
} from 'lucide-react';
import { TabLayout, ContentCard } from '../../components/ui/TabLayout';
import { ResponsiveTable, StatusBadge } from '../../components/ui/table';
import { Button } from '../../components/ui/Button/Button';
import { Filter } from '../../components/ui/Filter';

export function DriverDocumentsPage() {
  const { drivers } = useSelector(state => state.drivers);
  const [activeTab, setActiveTab] = useState('overview');
  const [filter, setFilter] = useState('');

  const documentColumns = [
    {
      key: 'name',
      header: 'Driver Name',
      primary: true,
      render: (_, item) => {
        const name = item?.name || `${item?.personalInfo?.firstName || ''} ${item?.personalInfo?.lastName || ''}`.trim();
        const email = item?.email || item?.personalInfo?.email || '';
        return (
          <div>
            <div className="font-medium text-gray-900">{name}</div>
            <div className="text-sm text-gray-500">{email}</div>
          </div>
        );
      }
    },
    {
      key: 'driverLicense',
      header: 'Driver License',
      render: (value) => (
        <StatusBadge 
          status={value?.status || 'missing'} 
          variant={
            value?.status === 'verified' ? 'success' :
            value?.status === 'expired' ? 'error' :
            value?.status === 'pending' ? 'warning' : 'default'
          } 
        />
      )
    },
    {
      key: 'insurance',
      header: 'Insurance',
      render: (value) => (
        <StatusBadge 
          status={value?.status || 'missing'} 
          variant={
            value?.status === 'verified' ? 'success' :
            value?.status === 'expired' ? 'error' :
            value?.status === 'pending' ? 'warning' : 'default'
          } 
        />
      )
    },
    {
      key: 'vehicleRegistration',
      header: 'Vehicle Registration',
      render: (value) => (
        <StatusBadge 
          status={value?.status || 'missing'} 
          variant={
            value?.status === 'verified' ? 'success' :
            value?.status === 'expired' ? 'error' :
            value?.status === 'pending' ? 'warning' : 'default'
          } 
        />
      )
    },
    {
      key: 'backgroundCheck',
      header: 'Background Check',
      render: (value) => (
        <StatusBadge 
          status={value?.status || 'missing'} 
          variant={
            value?.status === 'verified' ? 'success' :
            value?.status === 'failed' ? 'error' :
            value?.status === 'pending' ? 'warning' : 'default'
          } 
        />
      )
    }
  ];

  const documentActions = [
    {
      type: 'button',
      onClick: (item) => console.log('View documents:', item),
      label: 'View',
      variant: 'secondary',
      icon: Eye
    },
    {
      type: 'button',
      onClick: (item) => console.log('Download documents:', item),
      label: 'Download',
      variant: 'outline',
      icon: Download
    }
  ];

  const getFilteredDrivers = () => {
    return drivers.filter(driver => {
      const name = driver?.name || `${driver?.personalInfo?.firstName || ''} ${driver?.personalInfo?.lastName || ''}`.trim();
      const email = driver?.email || driver?.personalInfo?.email || '';
      const matchesFilter = name.toLowerCase().includes(filter.toLowerCase()) ||
                           email.toLowerCase().includes(filter.toLowerCase());
      
      switch (activeTab) {
        case 'complete':
          return matchesFilter && 
                 driver.driverLicense?.status === 'verified' &&
                 driver.insurance?.status === 'verified' &&
                 driver.vehicleRegistration?.status === 'verified' &&
                 driver.backgroundCheck?.status === 'verified';
        case 'incomplete':
          return matchesFilter && !(
            driver.driverLicense?.status === 'verified' &&
            driver.insurance?.status === 'verified' &&
            driver.vehicleRegistration?.status === 'verified' &&
            driver.backgroundCheck?.status === 'verified'
          );
        case 'expired':
          return matchesFilter && (
            driver.driverLicense?.status === 'expired' ||
            driver.insurance?.status === 'expired' ||
            driver.vehicleRegistration?.status === 'expired'
          );
        default:
          return matchesFilter;
      }
    });
  };

  const filteredDrivers = getFilteredDrivers();

  const tabs = [
    { id: 'overview', label: 'Document Overview', icon: FileText },
    { id: 'complete', label: 'Complete Documents', icon: CheckCircle },
    { id: 'incomplete', label: 'Incomplete Documents', icon: Clock },
    { id: 'expired', label: 'Expired Documents', icon: AlertTriangle }
  ];

  const getDocumentStats = () => {
    const complete = drivers.filter(d => 
      d.driverLicense?.status === 'verified' &&
      d.insurance?.status === 'verified' &&
      d.vehicleRegistration?.status === 'verified' &&
      d.backgroundCheck?.status === 'verified'
    ).length;
    
    const incomplete = drivers.filter(d => !(
      d.driverLicense?.status === 'verified' &&
      d.insurance?.status === 'verified' &&
      d.vehicleRegistration?.status === 'verified' &&
      d.backgroundCheck?.status === 'verified'
    )).length;
    
    const expired = drivers.filter(d => 
      d.driverLicense?.status === 'expired' ||
      d.insurance?.status === 'expired' ||
      d.vehicleRegistration?.status === 'expired'
    ).length;

    const pending = drivers.filter(d => 
      d.driverLicense?.status === 'pending' ||
      d.insurance?.status === 'pending' ||
      d.vehicleRegistration?.status === 'pending' ||
      d.backgroundCheck?.status === 'pending'
    ).length;

    return { complete, incomplete, expired, pending };
  };

  const stats = getDocumentStats();

  return (
    <TabLayout
      title="Document Management"
      subtitle="Manage driver licenses, insurance, and certification documents"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      {/* Document Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <ContentCard className="text-center">
          <div className="text-2xl font-bold text-green-600">
            {stats.complete}
          </div>
          <p className="text-sm text-gray-600 mt-1">Complete Documents</p>
        </ContentCard>
        <ContentCard className="text-center">
          <div className="text-2xl font-bold text-orange-600">
            {stats.incomplete}
          </div>
          <p className="text-sm text-gray-600 mt-1">Incomplete Documents</p>
        </ContentCard>
        <ContentCard className="text-center">
          <div className="text-2xl font-bold text-red-600">
            {stats.expired}
          </div>
          <p className="text-sm text-gray-600 mt-1">Expired Documents</p>
        </ContentCard>
        <ContentCard className="text-center">
          <div className="text-2xl font-bold text-blue-600">
            {stats.pending}
          </div>
          <p className="text-sm text-gray-600 mt-1">Pending Review</p>
        </ContentCard>
      </div>

      {/* Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex-1 max-w-md">
          <Filter
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name or email..."
          />
        </div>
        <Button variant="primary">
          <Upload size={16} className="mr-2" />
          Bulk Upload
        </Button>
      </div>

      {/* Documents Table */}
      <ResponsiveTable
        data={filteredDrivers}
        columns={documentColumns}
        actions={documentActions}
        emptyState={
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              No drivers with {activeTab} documents
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              There are currently no drivers in the {activeTab} documents category.
            </p>
          </div>
        }
      />

      {/* Document Types Overview */}
      <ContentCard title="Document Types Overview">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-xl font-bold text-blue-600">
              {drivers.filter(d => d.driverLicense?.status === 'verified').length}
            </div>
            <p className="text-sm text-gray-600 mt-1">Verified Licenses</p>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-green-600">
              {drivers.filter(d => d.insurance?.status === 'verified').length}
            </div>
            <p className="text-sm text-gray-600 mt-1">Valid Insurance</p>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-purple-600">
              {drivers.filter(d => d.vehicleRegistration?.status === 'verified').length}
            </div>
            <p className="text-sm text-gray-600 mt-1">Valid Registrations</p>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-orange-600">
              {drivers.filter(d => d.backgroundCheck?.status === 'verified').length}
            </div>
            <p className="text-sm text-gray-600 mt-1">Cleared Background</p>
          </div>
        </div>
      </ContentCard>

      {/* Quick Actions */}
      <ContentCard title="Document Actions">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button variant="primary" className="w-full">
            <Upload size={16} className="mr-2" />
            Upload Documents
          </Button>
          <Button variant="secondary" className="w-full">
            <CheckCircle size={16} className="mr-2" />
            Bulk Verify
          </Button>
          <Button variant="outline" className="w-full">
            <AlertTriangle size={16} className="mr-2" />
            Check Expiring
          </Button>
        </div>
      </ContentCard>
    </TabLayout>
  );
}
